import { Modal, ScrollArea, Stack, Table, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { ApiErrorAlert } from "./ApiErrorAlert.js";
import { useViewAsHistory } from "../hooks/useViewAs.js";

/**
 * „In meinem Namen gehandelt" — die Transparenzseite der übernommenen Sicht.
 *
 * Sie steht bewusst **jedem** offen und nicht nur Administratoren: Wenn jemand
 * unter fremdem Namen handeln kann, muss die betroffene Person das von sich aus
 * nachsehen können, ohne jemanden fragen zu müssen. Deshalb hängt der Einstieg
 * im Benutzermenü und nicht in den rollengeschützten Einstellungen.
 */
export function ViewAsHistoryModal({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { data, isLoading, error } = useViewAsHistory();
  const eintraege = data?.entries ?? [];

  const zeit = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: "short",
    timeStyle: "short",
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("viewAs.historyTitle")}
      size="lg"
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          {t("viewAs.historyIntro")}
        </Text>

        {error ? <ApiErrorAlert error={error} /> : null}
        {isLoading ? <Text size="sm">{t("common.loading")}</Text> : null}

        {!isLoading && eintraege.length === 0 ? (
          <Text size="sm">{t("viewAs.historyEmpty")}</Text>
        ) : null}

        {eintraege.length > 0 ? (
          <ScrollArea.Autosize mah={420}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("viewAs.historyWhen")}</Table.Th>
                  <Table.Th>{t("viewAs.historyWho")}</Table.Th>
                  <Table.Th>{t("viewAs.historyWhat")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {eintraege.map((e, i) => (
                  <Table.Tr key={`${e.occurredAt}-${i}`}>
                    <Table.Td>{zeit.format(new Date(e.occurredAt))}</Table.Td>
                    <Table.Td>
                      <Text size="sm">{e.byFullName ?? "—"}</Text>
                      <Text size="xs" c="dimmed">
                        {e.byEmail ?? ""}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{e.action}</Text>
                      <Text size="xs" c="dimmed">
                        {e.entityType}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>
        ) : null}
      </Stack>
    </Modal>
  );
}
